import { useState, useEffect, useRef } from 'react';
import { Palette, Check, RotateCcw } from 'lucide-react';

const presets = [
  { name: 'Navy & Gold', nameAr: 'كحلي وذهبي', primary: '#0F172A', accent: '#D4AF37' },
  { name: 'Ocean Blue', nameAr: 'أزرق محيطي', primary: '#1E3A5F', accent: '#38BDF8' },
  { name: 'Forest', nameAr: 'أخضر غابي', primary: '#14532D', accent: '#4ADE80' },
  { name: 'Royal Purple', nameAr: 'بنفسجي ملكي', primary: '#3B0764', accent: '#C084FC' },
  { name: 'Crimson', nameAr: 'أحمر قرمزي', primary: '#450A0A', accent: '#FB7185' },
  { name: 'Slate & Amber', nameAr: 'رمادي وكهرماني', primary: '#1E293B', accent: '#F59E0B' },
];

const defaultTheme = { primary: '#0F172A', accent: '#D4AF37' };

const lightenColor = (hex, amount) => {
  const num = parseInt(hex.replace('#', ''), 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
};

const darkenColor = (hex, amount) => lightenColor(hex, -amount);

const getContrastColor = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1a1a2e' : '#FFFFFF';
};

export const applyTheme = (theme) => {
  const root = document.documentElement;
  root.style.setProperty('--theme-primary', theme.primary);
  root.style.setProperty('--theme-accent', theme.accent);
  root.style.setProperty('--theme-accent-light', lightenColor(theme.accent, 100));
  root.style.setProperty('--theme-accent-hover', darkenColor(theme.accent, 30));
  root.style.setProperty('--theme-primary-foreground', getContrastColor(theme.primary));
  root.style.setProperty('--theme-accent-foreground', getContrastColor(theme.accent));
};

export const getTheme = () => {
  try {
    const saved = localStorage.getItem('appTheme');
    return saved ? JSON.parse(saved) : defaultTheme;
  } catch {
    return defaultTheme;
  }
};

export const ThemeCustomizer = ({ isRTL }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [theme, setTheme] = useState(getTheme);
  const panelRef = useRef(null);

  useEffect(() => {
    applyTheme(theme);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('appTheme', JSON.stringify(newTheme));
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="theme-customizer-btn"
        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        title={isRTL ? 'تخصيص الألوان' : 'Customize Colors'}
      >
        <Palette className="w-5 h-5 text-slate-600" />
      </button>

      {isOpen && (
        <div
          className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} w-72 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 z-50`}
          data-testid="theme-customizer-panel"
        >
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-sm font-semibold text-slate-800">
              {isRTL ? 'تخصيص الألوان' : 'Theme Colors'}
            </h3>
            <button
              onClick={() => updateTheme(defaultTheme)}
              data-testid="theme-reset-btn"
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
              title={isRTL ? 'إعادة تعيين' : 'Reset'}
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Presets */}
          <div className="mb-4">
            <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-2">{isRTL ? 'سمات جاهزة' : 'Presets'}</p>
            <div className="grid grid-cols-3 gap-1.5">
              {presets.map((preset) => {
                const isActive = theme.primary === preset.primary && theme.accent === preset.accent;
                return (
                  <button
                    key={preset.name}
                    onClick={() => updateTheme({ primary: preset.primary, accent: preset.accent })}
                    data-testid={`preset-${preset.name.replace(/\s/g, '-').toLowerCase()}`}
                    className={`relative flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all ${
                      isActive ? 'border-blue-400 bg-blue-50/50 shadow-sm' : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex gap-0.5">
                      <div className="w-5 h-5 rounded-full shadow-inner border border-black/10" style={{ backgroundColor: preset.primary }} />
                      <div className="w-5 h-5 rounded-full shadow-inner border border-black/10" style={{ backgroundColor: preset.accent }} />
                    </div>
                    <span className="text-[10px] text-slate-500 leading-tight text-center truncate w-full">
                      {isRTL ? preset.nameAr : preset.name}
                    </span>
                    {isActive && (
                      <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-2 h-2 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="space-y-2.5 pt-3 border-t border-slate-100">
            <p className="text-[11px] text-slate-400 uppercase tracking-wider">{isRTL ? 'ألوان مخصصة' : 'Custom'}</p>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <label className="text-xs text-slate-600 w-16 shrink-0">{isRTL ? 'الأساسي' : 'Primary'}</label>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="color"
                  value={theme.primary}
                  onChange={(e) => updateTheme({ ...theme, primary: e.target.value })}
                  data-testid="primary-color-input"
                  className="w-7 h-7 rounded cursor-pointer border-0 p-0"
                />
                <span className="text-[11px] font-mono text-slate-400">{theme.primary}</span>
              </div>
            </div>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <label className="text-xs text-slate-600 w-16 shrink-0">{isRTL ? 'التمييز' : 'Accent'}</label>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="color"
                  value={theme.accent}
                  onChange={(e) => updateTheme({ ...theme, accent: e.target.value })}
                  data-testid="accent-color-input"
                  className="w-7 h-7 rounded cursor-pointer border-0 p-0"
                />
                <span className="text-[11px] font-mono text-slate-400">{theme.accent}</span>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex gap-1.5 h-6 rounded-lg overflow-hidden">
              <div className="flex-1 rounded" style={{ backgroundColor: theme.primary }} />
              <div className="w-12 rounded" style={{ backgroundColor: theme.accent }} />
              <div className="w-8 rounded" style={{ backgroundColor: lightenColor(theme.accent, 100) }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
